import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";

/**
 * Tracks whether the current user has liked a specific post.
 * Returns real-time status.
 */
export function useLiked(clubId: string | undefined, postId: string | undefined, userId: string | undefined) {
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    if (!clubId || !postId || !userId) {
      setLiked(false);
      return;
    }

    const unsub = onSnapshot(
      doc(db, "clubs", clubId, "posts", postId, "likes", userId),
      (snap) => setLiked(snap.exists())
    );

    return unsub;
  }, [clubId, postId, userId]);

  return liked;
}

/**
 * Batch check likes for multiple posts (used in feed).
 * Returns a Set of postIds that the user has liked.
 */
export function useLikedPosts(clubId: string | undefined, postIds: string[], userId: string | undefined) {
  const [likedSet, setLikedSet] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!clubId || !userId || postIds.length === 0) {
      setLikedSet(new Set());
      return;
    }

    const unsubs: (() => void)[] = [];

    for (const postId of postIds) {
      const unsub = onSnapshot(
        doc(db, "clubs", clubId, "posts", postId, "likes", userId),
        (snap) => {
          setLikedSet((prev) => {
            const next = new Set(prev);
            if (snap.exists()) next.add(postId);
            else next.delete(postId);
            return next;
          });
        }
      );
      unsubs.push(unsub);
    }

    return () => unsubs.forEach((u) => u());
  }, [clubId, userId, postIds.join(",")]);

  return likedSet;
}
